// apps/web/src/components/layout/Layout.tsx
// 全局布局：底部导航 + 页面容器 — Phase 5 优化版

import { NavLink, useLocation } from "react-router-dom";
import { clsx } from "clsx";
import type { ReactNode } from "react";

// 底部导航配置
const TABS = [
  { to: "/", label: "首页", icon: HomeIcon, match: (p: string) => p === "/" },
  { to: "/banks", label: "题库", icon: BookIcon, match: (p: string) => p.startsWith("/banks") },
  { to: "/import", label: "AI+", icon: AIIcon, match: (p: string) => p === "/import" },
  { to: "/search", label: "搜索", icon: SearchIcon, match: (p: string) => p === "/search" },
  { to: "/profile", label: "我的", icon: UserIcon, match: (p: string) => p === "/profile" },
] as const;

export default function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  // 登录/注册/练习页隐藏底部导航
  const hideNav =
    pathname === "/login" ||
    pathname === "/register" ||
    pathname.includes("/practice/");

  if (hideNav) return <>{children}</>;

  return (
    <div className="flex flex-col min-h-screen bg-surface">
      {/* 主内容区 */}
      <main className="flex-1 overflow-y-auto pb-16">{children}</main>

      {/* 底部导航 */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md
                   border-t border-gray-100 flex items-stretch"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {TABS.map((tab) => {
          const active = tab.match(pathname);
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === "/"}
              className="flex-1 flex flex-col items-center justify-center py-1.5 gap-0.5"
            >
              <div
                className={clsx(
                  "flex items-center justify-center transition-all duration-200",
                  active ? "scale-110" : "scale-100"
                )}
              >
                <tab.icon active={active} />
              </div>
              <span
                className={clsx(
                  "text-[10px] transition-colors duration-200",
                  active
                    ? "text-primary font-semibold"
                    : "text-gray-400 font-normal"
                )}
              >
                {tab.label}
              </span>
              {/* 活跃指示器 */}
              <div
                className={clsx(
                  "absolute bottom-0.5 w-1 h-1 rounded-full transition-opacity duration-200",
                  active ? "bg-primary opacity-100" : "opacity-0"
                )}
              />
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

// ======== SVG 图标组件（活跃态填充）========

function HomeIcon({ active }: { active: boolean }) {
  const color = active ? "#3B82F6" : "#9CA3AF";
  if (active) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
        <path d="M9 21v-6h6v6" fill="white" stroke="none" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
      <polyline points="9 21 9 14 15 14 15 21" />
    </svg>
  );
}

function BookIcon({ active }: { active: boolean }) {
  const color = active ? "#3B82F6" : "#9CA3AF";
  if (active) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" fill="none" stroke="white" strokeWidth="1.5" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

function AIIcon({ active }: { active: boolean }) {
  const color = active ? "#3B82F6" : "#9CA3AF";
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? color : "none"} stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}

function SearchIcon({ active }: { active: boolean }) {
  const color = active ? "#3B82F6" : "#9CA3AF";
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={active ? "2.2" : "1.8"} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function UserIcon({ active }: { active: boolean }) {
  const color = active ? "#3B82F6" : "#9CA3AF";
  if (active) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="7" r="4" fill={color} />
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill={color} />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
